/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react'

import { useAvailableIncidentActions } from '../../hooks/useAvailableIncidentActions'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { startTimer, trackEvent } from '@/utils/analytics/useAnalytics'
import { useDispatch } from 'react-redux'

import { Box, Icon, keyframes, useDisclosure } from '@chakra-ui/react'

import { Share } from '@/components/icons'
import { Button } from '@/components/ui'
import {
  IncidentDocument,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentActionType, IncidentType } from '@/graphql/generated/schemas'
import { closeTraceLogDrawer, openSOPIncidentDrawer } from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'

import { ReleaseIncidentModal, TakeOverIncidentModal } from '../..'
import { SOP_AI_ASSIST_EVENT } from '../../sop-chatbot/types'
import { MixpanelDataIProps } from '../../types/types'

interface FeedButtonsIProps {
  incident: IncidentQuery['incident']
  mixpanelData: MixpanelDataIProps
}

const SOP_CTA_LABEL = 'What should I do?'

// Delay before dispatching sop-ai-assist event to allow the SOP drawer to open first
const SOP_DRAWER_OPEN_DELAY_MS = 300

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

interface AnimatedCtaButtonIProps {
  children: React.ReactNode
  onClick: () => void
}

const AnimatedCtaButton = ({ children, onClick }: AnimatedCtaButtonIProps) => (
  <Button
    _hover={{
      opacity: 0.9,
    }}
    animation={`${gradientShift} 3s ease infinite`}
    background='linear-gradient(270deg, #6366F1, #8B5CF6, #A855F7, #6366F1)'
    backgroundSize='300% 300%'
    color='white'
    data-testid='incidentDrawer_feed_aiAssist'
    onClick={onClick}
  >
    {children}
  </Button>
)

export const FeedButtons = ({ incident, mixpanelData }: FeedButtonsIProps) => {
  const { shouldEnableIncidentActions } = usePermissions()
  const { showSuccess, showError } = useToasts({ preventDuplicate: false })
  const { salesDatabaseReset: isSalesEnvironment } = useFeatureFlags()
  const shouldShowSopCta =
    isSalesEnvironment && incident?.type === IncidentType.DoorHeldOpen
  const dispatch = useDispatch()
  const [createIncidentAction, { loading: isLoadingCreateIncidentAction }] =
    useCreateIncidentActionMutation()

  const { isAcknowledgeEnabled, isCanReleaseEnabled, isTakeOverEnabled } =
    useAvailableIncidentActions({ incident })

  const incidentId = incident?.id

  const {
    isOpen: isTakeOverOpen,
    onOpen: onTakeOverOpen,
    onClose: onTakeOverClose,
  } = useDisclosure()
  const {
    isOpen: isReleaseOpen,
    onOpen: onReleaseOpen,
    onClose: onReleaseClose,
  } = useDisclosure()

  const aiAssistTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => clearTimeout(aiAssistTimerRef.current)
  }, [])

  const onAiAssistClick = useCallback(() => {
    dispatch(closeTraceLogDrawer())
    dispatch(openSOPIncidentDrawer())
    clearTimeout(aiAssistTimerRef.current)
    aiAssistTimerRef.current = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(SOP_AI_ASSIST_EVENT, {
          detail: { incidentId: incident.id },
        })
      )
    }, SOP_DRAWER_OPEN_DELAY_MS)
  }, [dispatch, incident?.id])

  const onAcknowledgementClick = async () => {
    const eventName = `${BUTTON_PRESS} Incident Acknowledge`
    startTimer(eventName)
    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.AcknowledgementByOperator,
          },
        },
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'GlobalUnresolvedIncidentsCount',
        ],
        // TODO - Remove this and make it async so it doesn't block the ack
        awaitRefetchQueries: true,
      })
      trackEvent(eventName, mixpanelData)
      showSuccess('Incident has been acknowledged.')
    } catch (e) {
      showError(e)
    }
  }

  const onReleaseClick = async () => {
    trackEvent(`${BUTTON_PRESS} Incident Release`, mixpanelData)
    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.ReleaseByOperator,
          },
        },
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'GlobalUnresolvedIncidentsCount',
        ],
        awaitRefetchQueries: true,
      })
      onReleaseClose()
      showSuccess('Incident has been released.')
    } catch (e) {
      showError(e)
    }
  }

  const onTakeOverClick = async () => {
    trackEvent(`${BUTTON_PRESS} Incident Take Over & Acknowledge`, mixpanelData)
    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.AcknowledgementByOperator,
          },
        },
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'Incidents',
        ],
        awaitRefetchQueries: true,
      })
      onTakeOverClose()
      showSuccess('Incident has been taken over & acknowledged.')
    } catch (e) {
      showError(e)
    }
  }
  return (
    <>
      {isAcknowledgeEnabled && (
        <Box
          bg='white'
          display={shouldShowSopCta ? 'flex' : 'block'}
          gap={3}
          mt='4'
          pos='relative'
        >
          <Button
            data-testid='incidentDrawer_feed_acknowledge'
            isDisabled={!shouldEnableIncidentActions}
            isLoading={isLoadingCreateIncidentAction}
            loadingText='Acknowledging...'
            onClick={onAcknowledgementClick}
          >
            Acknowledge
          </Button>
          {shouldShowSopCta && (
            <AnimatedCtaButton onClick={onAiAssistClick}>
              {SOP_CTA_LABEL}
            </AnimatedCtaButton>
          )}
        </Box>
      )}
      {isTakeOverEnabled && (
        <Box bg='#fff' mt='4' pos='relative'>
          <Button
            data-testid='incidentDrawer_feed_takeOverAndAcknowledge'
            isLoading={isLoadingCreateIncidentAction}
            loadingText='Taking over...'
            onClick={onTakeOverOpen}
          >
            Take Over & Acknowledge
          </Button>
        </Box>
      )}
      {isCanReleaseEnabled && (
        <Box bg='#fff' mt='4' pos='relative'>
          <Button
            data-testid='incidentDrawer_feed_release'
            leftIcon={<Icon as={Share} boxSize={6} strokeColor='#fff' />}
            loadingText='Releasing...'
            onClick={onReleaseOpen}
            variant='danger'
          >
            Release
          </Button>
        </Box>
      )}
      {isReleaseOpen && isCanReleaseEnabled && (
        <ReleaseIncidentModal
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoadingCreateIncidentAction}
          isOpen={isReleaseOpen}
          onClose={onReleaseClose}
          onSubmit={onReleaseClick}
        />
      )}
      {isTakeOverOpen && isTakeOverEnabled && (
        <TakeOverIncidentModal
          currentOwner={`${incident?.owner.firstName} ${incident?.owner.lastName}`}
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoadingCreateIncidentAction}
          isOpen={isTakeOverOpen}
          nextAction={IncidentActionType.AcknowledgementByOperator}
          onClose={onTakeOverClose}
          onSubmit={onTakeOverClick}
        />
      )}
    </>
  )
}
